/**
 * Quick-add syntax (spec §9.4), table-driven from the PropDefs:
 *
 *   title #tag @context @@place !2 ~3 ^30m due:fri every:2w group:home *someday prop:name=value
 *   with:Kim #when_we_meet   (circle mode, P2)
 *   (German and Spanish aliases: fällig:/vence:, alle:/cada:, gruppe:/grupo:, morgen/mañana, …)
 *   > a note body            (leading ">" makes a note)
 *   https://example.org      (a URL alone makes a note titled with the URL)
 *
 * The parser is deterministic and authoritative; the optional LLM fallback (plugin) only runs when
 * this one leaves the whole input as title. Unknown tokens stay in the title, never dropped.
 */
import type { PropDef, PropVal } from "./schema";

export interface QuickAddResult {
  title: string;
  typeId: "task" | "recurring" | "note";
  props: Record<string, PropVal>;
  /** tag names (without #) to resolve or create at apply time */
  tags: string[];
  /** group titles to resolve or create at apply time */
  groups: string[];
  notes?: string;
  /** tokens that looked like syntax but could not be resolved (shown as a hint, kept in title) */
  unresolved: string[];
  /** `@@hardware-store`: remind me when I am at this place (I9); resolved to a place at apply time */
  place?: string;
}

export interface QuickAddCtx {
  propDefs: PropDef[];
  now: Date;
}

const WEEKDAYS: Record<string, number> = {
  sun: 0,
  sunday: 0,
  so: 0,
  sonntag: 0,
  mon: 1,
  monday: 1,
  mo: 1,
  montag: 1,
  tue: 2,
  tuesday: 2,
  di: 2,
  dienstag: 2,
  wed: 3,
  wednesday: 3,
  mi: 3,
  mittwoch: 3,
  thu: 4,
  thursday: 4,
  do: 4,
  donnerstag: 4,
  fri: 5,
  friday: 5,
  fr: 5,
  freitag: 5,
  sat: 6,
  saturday: 6,
  sa: 6,
  samstag: 6,
  // Spanish ("do" stays Donnerstag; "mi" and "sa" mean the same day in both)
  domingo: 0,
  lunes: 1,
  lu: 1,
  martes: 2,
  ma: 2,
  miércoles: 3,
  miercoles: 3,
  jueves: 4,
  ju: 4,
  viernes: 5,
  vi: 5,
  sábado: 6,
  sabado: 6,
};

/** Parses a due token into an ISO datetime (local 09:00 unless a time is given), or null. */
export function parseDue(token: string, now: Date): string | null {
  const t = token.toLowerCase();
  const at = (d: Date, h = 9, m = 0) => {
    const x = new Date(d);
    x.setHours(h, m, 0, 0);
    return x.toISOString();
  };
  const base = new Date(now);
  if (t === "today" || t === "heute" || t === "hoy") return at(base);
  if (t === "tomorrow" || t === "morgen" || t === "mañana" || t === "manana") {
    // the next calendar day, not 24 hours on: around a clock change those differ (bug hunt M11)
    const d = new Date(base);
    d.setDate(d.getDate() + 1);
    return at(d);
  }
  const rel = t.match(/^\+(\d+)([dwm])$/);
  if (rel) {
    const n = Number(rel[1]);
    const d = new Date(base);
    if (rel[2] === "d") d.setDate(d.getDate() + n);
    else if (rel[2] === "w") d.setDate(d.getDate() + 7 * n);
    else d.setMonth(d.getMonth() + n);
    return at(d);
  }
  if (t in WEEKDAYS) {
    const target = WEEKDAYS[t];
    const d = new Date(base);
    const diff = (target - d.getDay() + 7) % 7 || 7; // next occurrence, never today
    d.setDate(d.getDate() + diff);
    return at(d);
  }
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:t(\d{2}):(\d{2}))?$/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    if (Number.isNaN(d.getTime())) return null;
    return at(d, iso[4] ? Number(iso[4]) : 9, iso[5] ? Number(iso[5]) : 0);
  }
  const dm = t.match(/^(\d{1,2})\.(\d{1,2})\.?$/); // 3.10. (de)
  if (dm) {
    const d = new Date(base.getFullYear(), Number(dm[2]) - 1, Number(dm[1]));
    // a date earlier than today means next year; today itself stays today
    const today = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    if (d < today) d.setFullYear(d.getFullYear() + 1);
    return at(d);
  }
  return null;
}

/** `30m`, `1h`, `1h30m`, `90` (minutes), `1.5h` → minutes, or null. */
export function parseMinutes(token: string): number | null {
  const t = token.toLowerCase();
  if (/^\d+$/.test(t)) return Number(t);
  const m = t.match(/^(?:(\d+(?:\.\d+)?)h)?(?:(\d+)m)?$/);
  if (!m || (!m[1] && !m[2])) return null;
  return Math.round((m[1] ? Number(m[1]) * 60 : 0) + (m[2] ? Number(m[2]) : 0));
}

/** `2w`, `7d`, `3`, `month`, `2mo`, `week`, `daily`, `weekly`, `30m` (minutes), `2h` → days, or null. `m` is
 * minutes, as in `^30m` (it was months: one letter with two meanings); months are `mo`. */
export function parsePeriodDays(token: string): number | null {
  const t = token.toLowerCase();
  if (t === "day" || t === "daily" || t === "täglich" || t === "diario") return 1;
  if (t === "week" || t === "weekly" || t === "wöchentlich" || t === "semanal") return 7;
  if (t === "month" || t === "monthly" || t === "monatlich" || t === "mensual") return 30;
  if (t === "year" || t === "yearly" || t === "jährlich" || t === "anual") return 365;
  const m = t.match(/^(\d+(?:\.\d+)?)(d|w|mo|m|h|y)?$/);
  if (!m) return null;
  const n = Number(m[1]);
  switch (m[2]) {
    case "w":
      return n * 7;
    case "mo":
      return n * 30;
    case "m":
      return n / 1440;
    case "h":
      return n / 24;
    case "y":
      return n * 365;
    default:
      return n;
  }
}

/** The unit a period was written in, kept beside the days so the task shows it again ("2 weeks", not "14
 * days"); days (and years, which have no unit) give null. Same rule in quickadd.py. */
export function parsePeriodUnit(token: string): "minutes" | "hours" | "weeks" | "months" | null {
  const t = token.toLowerCase();
  if (["week", "weekly", "wöchentlich", "semanal"].includes(t) || /^\d+(?:\.\d+)?w$/.test(t)) return "weeks";
  if (["month", "monthly", "monatlich", "mensual"].includes(t) || /^\d+(?:\.\d+)?mo$/.test(t))
    return "months";
  if (/^\d+(?:\.\d+)?m$/.test(t)) return "minutes";
  if (/^\d+(?:\.\d+)?h$/.test(t)) return "hours";
  return null;
}

function enumValueId(def: PropDef, raw: string): string | null {
  const values = (def.spec.values as { id: string; label: string; aliases?: string[] }[] | undefined) ?? [];
  const r = raw.toLowerCase().replace(/^@/, "");
  for (const v of values) {
    // id, label, and the value's aliases: other words for it (`@zuhause`, `@fuera`), same as quickadd.py
    const words = [v.id, v.label.replace(/^@/, ""), ...(v.aliases ?? [])];
    if (words.some((w) => w.toLowerCase() === r)) return v.id;
  }
  return null;
}

function setEnum(props: Record<string, PropVal>, def: PropDef, valueId: string): void {
  if (def.spec.multi) {
    const cur = (props[def.id] as string[] | undefined) ?? [];
    if (!cur.includes(valueId)) props[def.id] = [...cur, valueId];
  } else props[def.id] = valueId;
}

const PRIORITY: Record<string, string> = {
  "1": "high",
  p1: "high",
  high: "high",
  hoch: "high",
  alta: "high",
  "2": "normal",
  p2: "normal",
  normal: "normal",
  "3": "low",
  p3: "low",
  low: "low",
  niedrig: "low",
  baja: "low",
};

/** `#when_we_meet` (P2): "remind me when we meet" for the people in `with:`, not a tag */
const WHEN_WE_MEET = new Set(["when_we_meet", "wenn_wir_uns_sehen", "cuando_nos_veamos"]);
// the horizon only with a "*" in front: a bare "next" or "someday" in a title stays in it (no surprises)
const SOMEDAY = new Set(["*someday", "*irgendwann", "*algún_día", "*algun_dia"]);
const NEXT = new Set(["*next", "*nächstes", "*später", "*siguiente", "*después", "*despues"]);

export function parseQuickAdd(input: string, ctx: QuickAddCtx): QuickAddResult {
  const res: QuickAddResult = { title: "", typeId: "task", props: {}, tags: [], groups: [], unresolved: [] };
  let text = input.trim();
  if (text.startsWith(">")) {
    res.typeId = "note";
    text = text.slice(1).trim();
  }
  if (/^https?:\/\/\S+$/i.test(text)) {
    res.typeId = "note";
    res.title = text;
    res.notes = text;
    return res;
  }
  const byName = new Map(ctx.propDefs.map((d) => [d.name.toLowerCase(), d]));
  const byId = new Map(ctx.propDefs.map((d) => [d.id, d]));
  const words: string[] = [];
  for (const tok of text.split(/\s+/).filter(Boolean)) {
    const lower = tok.toLowerCase();
    let handled = false;
    if (tok.length > 1 && tok.startsWith("#") && WHEN_WE_MEET.has(lower.slice(1))) {
      res.props.whenWeMeet = true;
      handled = true;
    } else if (tok.length > 1 && tok.startsWith("#")) {
      res.tags.push(tok.slice(1));
      handled = true;
    } else if (tok.length > 2 && tok.startsWith("@@")) {
      res.place = tok.slice(2).replace(/[-_]+/g, " ").trim();
      handled = true;
    } else if (tok.length > 1 && tok.startsWith("@")) {
      const ctxDef = byId.get("context");
      const vid = ctxDef ? enumValueId(ctxDef, tok) : null;
      if (ctxDef && vid) {
        setEnum(res.props, ctxDef, vid);
        handled = true;
      } else res.unresolved.push(tok);
    } else if (tok.length > 1 && tok.startsWith("!")) {
      const p = PRIORITY[lower.slice(1)];
      if (p) {
        res.props.priority = p;
        handled = true;
      } else res.unresolved.push(tok);
    } else if (/^~[1-5]$/.test(tok)) {
      res.props.energy = Number(tok.slice(1));
      handled = true;
    } else if (tok.startsWith("^") && tok.length > 1) {
      const m = parseMinutes(tok.slice(1));
      if (m !== null) {
        res.props.estimateMin = m;
        handled = true;
      } else res.unresolved.push(tok);
    } else if (lower.startsWith("due:") || lower.startsWith("fällig:") || lower.startsWith("vence:")) {
      const d = parseDue(tok.slice(tok.indexOf(":") + 1), ctx.now);
      if (d) {
        res.props.due = d;
        handled = true;
      } else res.unresolved.push(tok);
    } else if (["every:", "alle:", "cada:"].some((p) => lower.startsWith(p))) {
      const raw = tok.slice(tok.indexOf(":") + 1);
      const p = parsePeriodDays(raw);
      if (p !== null && p > 0) {
        res.typeId = res.typeId === "note" ? "note" : "recurring";
        res.props["rec.periodDays"] = p;
        const unit = parsePeriodUnit(raw);
        if (unit) res.props["rec.periodUnit"] = unit;
        handled = true;
      } else res.unresolved.push(tok);
    } else if (["with:", "mit:", "con:"].some((p) => lower.startsWith(p)) && byId.has("with")) {
      // the people a task is about (P2), by name; `_` and `-` stand for spaces
      const def = byId.get("with")!;
      const raw = tok.slice(tok.indexOf(":") + 1);
      const vid = enumValueId(def, raw) ?? enumValueId(def, raw.replace(/[-_]+/g, " "));
      if (vid) {
        setEnum(res.props, def, vid);
        handled = true;
      } else res.unresolved.push(tok);
    } else if (["group:", "gruppe:", "grupo:"].some((p) => lower.startsWith(p))) {
      const g = tok.slice(tok.indexOf(":") + 1);
      if (g) {
        res.groups.push(g);
        handled = true;
      }
    } else if (SOMEDAY.has(lower) || NEXT.has(lower)) {
      res.props.horizon = NEXT.has(lower) ? "next" : "someday";
      handled = true;
    } else if (lower.startsWith("prop:") || lower.startsWith("p:")) {
      const body = tok.slice(tok.indexOf(":") + 1);
      const eq = body.indexOf("=");
      const name = (eq >= 0 ? body.slice(0, eq) : body).toLowerCase();
      const raw = eq >= 0 ? body.slice(eq + 1) : "";
      // hyphens and underscores stand for spaces, as in @@place: `prop:not-before=fri`
      const def = byName.get(name) ?? byName.get(name.replace(/[-_]+/g, " ")) ?? byId.get(name);
      if (def) {
        handled = applyPropToken(res.props, def, raw, ctx.now);
        if (!handled) res.unresolved.push(tok);
      } else res.unresolved.push(tok);
    }
    if (!handled) words.push(tok);
  }
  res.title = words.join(" ").trim();
  if (res.typeId === "note") {
    res.notes = res.title;
    res.title = res.title.split("\n")[0];
  }
  return res;
}

function applyPropToken(props: Record<string, PropVal>, def: PropDef, raw: string, now: Date): boolean {
  switch (def.kind) {
    case "tag":
      props[def.id] = true;
      return true;
    case "number": {
      const n = Number(raw);
      if (raw.trim() === "" || Number.isNaN(n)) return false; // Number("") would be 0
      props[def.id] = n;
      return true;
    }
    case "text":
      props[def.id] = raw;
      return true;
    case "datetime": {
      const d = parseDue(raw, now);
      if (!d) return false;
      props[def.id] = d;
      return true;
    }
    case "enum": {
      const vid = enumValueId(def, raw);
      if (!vid) return false;
      setEnum(props, def, vid);
      return true;
    }
    case "geo":
      return false;
  }
}
